/*
  Isekaid Sword-Slash Page Transition
  Faster Original Version
  File: assets/isekaid-page-transition.js
*/

(() => {
  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  if (reducedMotion.matches) {
    return;
  }

  const STORAGE_KEY =
    'isekaid-page-transition-pending';

  /*
    Faster than the original version while still leaving
    enough time for the panels, slash, and logo to register.
  */
  const TIMING = {
    slashStart: 265,
    brandStart: 295,
    navigateAt: 590,
    openingDelay: 45,
    cleanupAfterOpening: 570
  };

  let transitionRunning = false;
  let destinationUrl = null;

  /* =======================================================
     CREATE OVERLAY
     ======================================================= */

  const overlay = document.createElement('div');

  overlay.className = 'isekaid-page-transition';
  overlay.setAttribute('aria-hidden', 'true');

  overlay.innerHTML = `
    <div
      class="
        isekaid-page-transition__panel
        isekaid-page-transition__panel--left
      "
    ></div>

    <div
      class="
        isekaid-page-transition__panel
        isekaid-page-transition__panel--right
      "
    ></div>

    <div class="isekaid-page-transition__flash"></div>

    <div class="isekaid-page-transition__brand">
      <span class="isekaid-page-transition__fallback-logo">
        Isekaid Apparel
      </span>
    </div>

    <div class="isekaid-page-transition__slash"></div>

    <div
      class="isekaid-page-transition__slash-secondary"
    ></div>
  `;

  document.body.appendChild(overlay);

  const brandContainer = overlay.querySelector(
    '.isekaid-page-transition__brand'
  );

  const installLogo = () => {
    if (!brandContainer) {
      return;
    }

    const logo = document.createElement('img');

    logo.className =
      'isekaid-page-transition__logo';

    logo.src = '/cdn/shop/files/isekaid_apparel_logo_WHITE_transparent.png?v=1785298474&width=1000';
    logo.width = 840;
    logo.height = 173;
    logo.decoding = 'async';

    logo.alt = 'Isekaid Apparel';

    brandContainer.replaceChildren(logo);
  };

  installLogo();

  const INTRO_SEEN_KEY = 'isekaid-summoning-gate-seen';
  const INTRO_PENDING_CLASS = 'isekaid-intro-pending';
  let introRunning = false;

  const playIntro = () => {
    if (document.documentElement.classList.contains('shopify-design-mode')) return false;
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === '1' || sessionStorage.getItem(INTRO_SEEN_KEY) === '1') return false;
      sessionStorage.setItem(INTRO_SEEN_KEY, '1');
    } catch { return false; }

    const glyphs = Array.from({ length: 12 }, (_, i) => {
      const marks = [
        '<path d="M-9 0 0-12 9 0 0 12Z M-4 0h8 M0-5v10"/>',
        '<path d="M-9 8V-7L0-12 9-7V8 M-9 2h18 M-5 8l5-9 5 9"/>',
        '<path d="M-10-7 0 10 10-7 M-6-7h12 M0-9v19 M-5 4h10"/>'
      ];
      return '<g class="isekaid-entry__glyph" style="--delay:' + (i * 48) + 'ms" transform="rotate(' + (i * 30) + ') translate(0 -179)">' + marks[i % 3] + '</g>';
    }).join('');
    const motes = Array.from({ length: 20 }, (_, i) =>
      '<span class="isekaid-entry__mote" style="--a:' + (i * 18) + 'deg;--delay:' + (i % 5 * 52) + 'ms"></span>'
    ).join('');

    const gate = document.createElement('div');
    gate.className = 'isekaid-entry';
    gate.setAttribute('aria-hidden', 'true');
    gate.innerHTML = `
      <div class="isekaid-entry__stars"></div>
      <div class="isekaid-entry__aura"></div>
      <div class="isekaid-entry__motes">${motes}</div>
      <svg class="isekaid-entry__seal" viewBox="-240 -240 480 480" fill="none" aria-hidden="true">
        <g class="isekaid-entry__outer">
          <circle r="205" stroke-dasharray="2 10"/><circle r="196"/><circle r="188" stroke-dasharray="26 7 2 7"/>
          <path d="M0-216 8-204 0-192-8-204Z M216 0 204 8 192 0 204-8Z M0 216-8 204 0 192 8 204Z M-216 0-204-8-192 0-204 8Z"/>
          ${glyphs}
        </g>
        <g class="isekaid-entry__middle">
          <circle r="151"/><circle r="143" stroke-dasharray="34 9 3 9"/>
          <path d="M0-166 144 83-144 83Z M0 166 144-83-144-83Z M0-153v-23 M153 0h23 M0 153v23 M-153 0h-23"/>
        </g>
        <g class="isekaid-entry__inner">
          <circle r="113"/><circle r="108" stroke-dasharray="1 6"/>
          <path d="M-79-79 79-79 79 79-79 79Z M0-112 112 0 0 112-112 0Z"/><circle r="86"/>
        </g>
      </svg>
      <span class="isekaid-entry__core"></span>
      <img class="isekaid-entry__logo" src="/cdn/shop/files/isekaid_apparel_logo_WHITE_transparent.png?v=1785298474&width=1000" width="840" height="173" alt="" decoding="async">
      <span class="isekaid-entry__wave"></span><span class="isekaid-entry__flash"></span>
    `;

    document.body.appendChild(gate);
    document.documentElement.classList.remove(INTRO_PENDING_CLASS);
    document.documentElement.style.overflow = 'hidden';
    introRunning = true;
    requestAnimationFrame(() => gate.classList.add('is-awakening'));
    window.setTimeout(() => gate.classList.add('is-unsealing'), 1740);
    window.setTimeout(() => {
      gate.remove();
      introRunning = false;
      document.documentElement.style.removeProperty('overflow');
    }, 2620);
    return true;
  };

  if (!playIntro()) document.documentElement.classList.remove(INTRO_PENDING_CLASS);

  /* =======================================================
     LINKS THAT SHOULD NOT USE THE TRANSITION
     ======================================================= */

  const SKIP_CLOSEST_SELECTOR = [
    '[data-no-page-transition]',
    '.isekaid-no-transition',
    '.shopify-payment-button',
    '.cart-drawer',
    '.menu-drawer',
    '.predictive-search',
    '[data-shopify-editor-block]',
    '[data-shopify-editor-section]'
  ].join(',');

  const shouldTransitionLink = (
    link,
    event
  ) => {
    if (!(link instanceof HTMLAnchorElement)) {
      return false;
    }

    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return false;
    }

    if (
      link.closest(SKIP_CLOSEST_SELECTOR) ||
      link.hasAttribute('download') ||
      link.target === '_blank'
    ) {
      return false;
    }

    const rawHref = link.getAttribute('href');

    if (
      !rawHref ||
      rawHref.startsWith('#') ||
      rawHref.startsWith('mailto:') ||
      rawHref.startsWith('tel:') ||
      rawHref.startsWith('sms:') ||
      rawHref.startsWith('javascript:')
    ) {
      return false;
    }

    let url;

    try {
      url = new URL(
        link.href,
        window.location.href
      );
    } catch {
      return false;
    }

    if (
      url.origin !==
      window.location.origin
    ) {
      return false;
    }

    if (
      url.pathname ===
        window.location.pathname &&
      url.search ===
        window.location.search &&
      url.hash
    ) {
      return false;
    }

    if (
      url.pathname.startsWith('/admin') ||
      url.pathname.startsWith(
        '/services/login_with_shop'
      )
    ) {
      return false;
    }

    return true;
  };

  /* =======================================================
     EXIT TRANSITION
     ======================================================= */

  const beginExitTransition = (url) => {
    if (transitionRunning || introRunning) {
      return;
    }

    transitionRunning = true;
    destinationUrl = url;

    document.documentElement.style.overflow =
      'hidden';

    overlay.classList.remove(
      'is-entering',
      'is-opening',
      'is-slashing',
      'is-brand-visible'
    );

    overlay.classList.add(
      'is-active',
      'is-closing'
    );

    window.setTimeout(() => {
      overlay.classList.add('is-slashing');
    }, TIMING.slashStart);

    window.setTimeout(() => {
      overlay.classList.add(
        'is-brand-visible'
      );
    }, TIMING.brandStart);

    window.setTimeout(() => {
      try {
        sessionStorage.setItem(
          STORAGE_KEY,
          '1'
        );
      } catch {
        /*
          Navigation still works when storage
          is unavailable.
        */
      }

      window.location.assign(destinationUrl);
    }, TIMING.navigateAt);
  };

  document.addEventListener(
    'click',
    (event) => {
      const link =
        event.target.closest?.('a[href]');

      if (
        !shouldTransitionLink(
          link,
          event
        )
      ) {
        return;
      }

      event.preventDefault();

      beginExitTransition(link.href);
    }
  );

  /* =======================================================
     DESTINATION-PAGE OPENING
     ======================================================= */

  const shouldPlayEntrance = () => {
    try {
      const pending =
        sessionStorage.getItem(
          STORAGE_KEY
        ) === '1';

      if (pending) {
        sessionStorage.removeItem(
          STORAGE_KEY
        );
      }

      return pending;
    } catch {
      return false;
    }
  };

  const playEntranceTransition = () => {
    overlay.classList.remove(
      'is-active',
      'is-closing',
      'is-slashing'
    );

    overlay.classList.add(
      'is-entering',
      'is-brand-visible'
    );

    document.documentElement.style.overflow =
      'hidden';

    /*
      Paint the closed panels first, then
      immediately begin opening them.
    */
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          overlay.classList.remove(
            'is-entering',
            'is-brand-visible'
          );

          overlay.classList.add(
            'is-active',
            'is-opening'
          );
        }, TIMING.openingDelay);
      });
    });

    window.setTimeout(() => {
      overlay.classList.remove(
        'is-active',
        'is-opening'
      );

      document.documentElement.style
        .removeProperty('overflow');
    }, TIMING.cleanupAfterOpening);
  };

  if (shouldPlayEntrance()) {
    playEntranceTransition();
  }

  /* =======================================================
     BACK/FORWARD CACHE CLEANUP
     ======================================================= */

  window.addEventListener(
    'pageshow',
    (event) => {
      if (!event.persisted) {
        return;
      }

      transitionRunning = false;
      destinationUrl = null;

      overlay.className =
        'isekaid-page-transition';

      document.querySelector('.isekaid-entry')?.remove();
      introRunning = false;
      document.documentElement.classList.remove(INTRO_PENDING_CLASS);

      document.documentElement.style
        .removeProperty('overflow');

      document.documentElement.classList.remove(
        'isekaid-transition-pending'
      );
    }
  );

  /* =======================================================
     ESCAPE SAFETY
     ======================================================= */

  document.addEventListener(
    'keydown',
    (event) => {
      if (
        event.key !== 'Escape' ||
        !transitionRunning
      ) {
        return;
      }

      transitionRunning = false;
      destinationUrl = null;

      overlay.className =
        'isekaid-page-transition';

      document.documentElement.style
        .removeProperty('overflow');

      document.documentElement.classList.remove(
        'isekaid-transition-pending'
      );
    }
  );
})();
